import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Close, InfoOutlined } from '@material-ui/icons'
import { Label, TextField, SelectList, CleanButton, Checkbox } from '../default'
import { Box, Grid, IconButton, Modal, Paper, Tooltip, InputAdornment, withStyles } from '@material-ui/core'
import { connect, useDispatch } from 'react-redux'

import { Loader, Alert, PrimaryButton } from '../../components/default'
import { acClearAddedPlans, acGetPlanTables, acPostAddPlanToPlanTable, acPostCreatePlan } from '../../class/actions'
import { DatePicker } from '@material-ui/pickers'

function CreatePlanModal({
  classes, open, onClose,
  loading, fetchError,
  planTables, planTablesError, loadingPlanTables
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [description, setDescription] = useState('')
  const [discount, setDiscount] = useState('')
  const [discountError, setDiscountError] = useState('')
  const [validUntil, setValidUntil] = useState(null)
  const [validUntilError, setValidUntilError] = useState('')
  const [isTrial, setIsTrial] = useState(false)
  const [isTransitionOnly, setIsTransitionOnly] = useState(false)
  const [error, setError] = useState(null)
  const [planTablesIncluded, setPlanTablesIncluded] = useState([null])

  useEffect(() => {
    dispatch(acGetPlanTables())
    dispatch(acClearAddedPlans())
  }, [dispatch])

  const createPlan = () => {
    if (planTablesIncluded.filter(planTable => planTable != null).length && planTablesIncluded.length > new Set(planTablesIncluded.map(planTable => planTable ? planTable.plan_table_id : -1)).size) {
      setError(t('modals.plans.create.errorRepeatedPlanTables'))
      return
    }

    if (!name || nameError) {
      setNameError(t('modals.plans.create.missingName'))
      return
    }

    if (!discount || discountError) {
      setDiscountError(t('modals.plans.create.missingDiscount'))
      return
    }

    if (!validUntil) {
      setValidUntilError(t('modals.plans.create.missingValidUntil'))
      return
    }

    setError(null)

    dispatch(acPostCreatePlan(name, validUntil.toISOString().split('T')[0], description, discount / 100, true, isTrial, isTransitionOnly))
      .then(plan => {
        if (!planTablesIncluded.filter(planTable => planTable != null).length) {
          onClose()
          setInicialState()
          return
        }

        Promise.all(planTablesIncluded.filter(planTable => planTable != null).map(newPlanTable => {
          return new Promise((resolve, reject) => {
            dispatch(acPostAddPlanToPlanTable(newPlanTable.plan_table_id, plan.plan_id, newPlanTable.price))
              .then(value => resolve(value))
              .catch(err => reject(err))
          })
        })).then(() => {
          onClose()
          setInicialState()
        })
      })
      .catch(err => {
        console.log(err)
        setError(t('modals.plans.create.unknownError'))
      })
  }

  const setInicialState = () => {
    setName('')
    setDescription('')
    setDiscount('')
    setValidUntil(null)
    setIsTrial(false)
    setIsTransitionOnly(false)
    setPlanTablesIncluded([null])
  }

  const validateName = value => {
    setName(value)
    if (value === "") {
      setNameError(t('modals.plans.create.missingName'))
      return
    }
    setNameError('')
  }

  const validateDiscount = value => {

    if (value === "") {
      setDiscount(value)
      setDiscountError(t('modals.plans.create.missingDiscount'))
      return
    }

    if (isNaN(value)) {
      setDiscountError(t('modals.plans.create.invalidDiscount'))
      return
    }

    const numericValue = parseFloat(value)

    if (numericValue < 1 || numericValue > 100) {
      setDiscountError(t('modals.plans.create.invalidDiscountInterval'))
      setDiscount(numericValue)
      return
    }

    setDiscountError(null)
    setDiscount(numericValue)

  }

  const handleSelectListChange = data => {
    setPlanTablesIncluded(data)
  }

  const handleIsTrialClick = value => {
    if (value) {
      setIsTrial(true)
    } else {
      setIsTrial(false)
      setIsTransitionOnly(false)
    }
  }

  const handleIsTransitionOnlyClick = value => {
    if (value) {
      setIsTransitionOnly(true)
      setIsTrial(true)
    } else {
      setIsTransitionOnly(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus style={{ outline: 0, overflowY: 'scroll' }}>
      <Paper
        square={true}
        className={classes.root + ' ' + classes.modalDiv}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Grid container spacing={2} className={classes.grid}>
            <Grid item xs={8}>
              <Label v='h6'>
                {t('modals.plans.create.title')}
              </Label>
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
              <Tooltip title={t('default.close')}>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <TextField
                light
                variant='filled'
                type="text"
                label={t('modals.plans.name')}
                value={name}
                onChange={e => validateName(e.target.value)}
                error={nameError ? true : false}
                helperText={nameError ? nameError : null}
                style={{ width: '100%' }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                light
                variant='filled'
                type="text"
                label={t('modals.plans.description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: '100%' }} />
            </Grid>

            <Grid item xs={6}>
              <TextField
                light
                variant='filled'
                type="number"
                label={t('modals.plans.discount')}
                value={discount}
                onChange={e => validateDiscount(e.target.value)}
                error={discountError ? true : false}
                helperText={discountError ? discountError : null}
                style={{ width: '100%' }}
                InputProps={{
                  endAdornment:
                    <InputAdornment position="end">%</InputAdornment>
                }}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <div className={classes.tooltipContainer}>
                <DatePicker
                  label={t('modals.plans.validUntil')}
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  value={validUntil}
                  onChange={e => {
                    setValidUntil(e)
                    setValidUntilError('')
                  }}
                  error={validUntilError ? true : false}
                  helperText={validUntilError ? validUntilError : null}
                  style={{ width: '100%' }}
                  required
                />
              </div>
              <Tooltip title={t('modals.plans.validUntilTooltip')} style={{ marginLeft: '10px' }}>
                <InfoOutlined fontSize='small' />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Checkbox
                label={t('modals.plans.isTrial')}
                checked={isTrial}
                onChange={() => handleIsTrialClick(!isTrial)}
              />
              <Tooltip title={t('modals.plans.isTrialTooltip')} style={{ marginLeft: '10px' }}>
                <InfoOutlined fontSize='small' />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Checkbox
                label={t('modals.plans.isTransitionOnly')}
                checked={isTransitionOnly}
                onChange={() => handleIsTransitionOnlyClick(!isTransitionOnly)}
              />
              <Tooltip title={t('modals.plans.isTransitionOnlyTooltip')} style={{ marginLeft: '10px' }}>
                <InfoOutlined fontSize='small' />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Label bold>
                    {t('modals.plans.addToPlanTables')}
                  </Label>
                </Grid>
                <Grid item xs={12}>
                  <SelectList
                    label={t('modals.plans.planTable')}
                    selectItems={planTables.map(planTable => ({
                      name: planTable.name,
                      value: planTable.plan_table_id
                    }))}
                    extraDataKey='price'
                    valueKey='plan_table_id'
                    extraDataDefaultValue={0}
                    extraDataParseFunction={value => parseFloat(value)}
                    onChange={handleSelectListChange} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <div>
          <div style={{ marginTop: '15px' }}>
            {
              (fetchError) ? (
                <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={fetchError.description ? fetchError.description : t('modals.plans.create.unknownFetchError')} />
              )
                : (planTablesError) ? (
                  <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={planTablesError.description ? planTablesError.description : t('modals.plans.create.unknownPlanTableError')} />
                )
                  : error ?
                    <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={error} /> : null
            }
          </div>
          <div style={{ marginTop: '15px' }}>
            <Grid container style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CleanButton onClick={onClose} style={{ marginRight: '15px' }}>
                {t('default.cancel')}
              </CleanButton>
              <PrimaryButton onClick={createPlan}>
                {
                  (loading || loadingPlanTables) ? (
                    <Loader />
                  ) : (
                    <div>
                      {t('modals.plans.createPlan')}
                    </div>
                  )
                }
              </PrimaryButton>
            </Grid>
          </div>
        </div>
      </Paper>
    </Modal>
  )
}

CreatePlanModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  close: PropTypes.func
}

CreatePlanModal.defaultProps = {
  open: false,
  onClose: () => true,
  close: () => true
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    padding: theme.spacing(2),
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  modalDiv: {
    [theme.breakpoints.up('md')]: {
      width: '50%'
    },
    width: '100%',
    top: '10%',
    left: '50%',
    transform: 'translate(-50%, -10%)',
  },
  tooltipContainer: {
    display: 'inline-block',
    width: 'calc(100% - 35px)',
  }
})

const mapStateToProps = ({ ReducerFinancial, ReducerPlanTable }) => ({
  loading: ReducerFinancial.loading,
  fetchError: ReducerFinancial.error,
  payment: ReducerFinancial.payment,

  planTables: ReducerPlanTable.planTables,
  planTablesError: ReducerPlanTable.planTablesError,
  loadingPlanTables: ReducerPlanTable.loadingPlanTables,
})

export default connect(mapStateToProps)(withStyles(styles)(CreatePlanModal))